import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ExternalLink,
  FolderOpen,
  LayoutDashboard,
  Loader2,
  Menu,
  Newspaper,
  Settings2,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { usePortalNavEstado, type PortalNavItem } from "@/hooks/usePortalNav";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { ENVIRONMENT } from "@/lib/config";
import { EstatusAvanceChip } from "@/components/admin/EstatusAvanceChip";
import { SocioProyectoProvider, useSocioProyecto } from "./SocioProyectoContext";
import { SocioImpersonationProvider, useSocioImpersonation } from "./SocioImpersonationContext";
import { SocioImpersonationBanner, SocioImpersonationSelector } from "./SocioImpersonationSelector";
import { ProgressBar } from "./ui";

export const PORTAL_SOCIOS_BASE = "/admin/portal-socio";
export const PORTAL_SOCIOS_CONFIG_PATH = `${PORTAL_SOCIOS_BASE}/configuracion`;
const PROPIEDADES_URL = "https://socios-propiedades.sozu.com";

/** Menú "Portal Socio" en BD: etiquetas, orden y alta/baja de vistas viven en `submenus`. */
const SOCIOS_MENU_ID = 37;

/** El ícono no vive en BD: se resuelve por ruta. */
const iconMap: Record<string, LucideIcon> = {
  [PORTAL_SOCIOS_BASE]: LayoutDashboard,
  [`${PORTAL_SOCIOS_BASE}/actualizaciones`]: Newspaper,
  [`${PORTAL_SOCIOS_BASE}/repositorio`]: FolderOpen,
  [PORTAL_SOCIOS_CONFIG_PATH]: Settings2,
};

/**
 * Abre la app de propiedades (socios-propiedades.sozu.com) con SSO.
 * La pestaña se abre SÍNCRONA en el click (si no, el bloqueador de popups la mata) y
 * se redirige cuando la EF devuelve el magic link. Si algo falla, cae al sitio sin SSO.
 */
function useVerMisPropiedades() {
  const [abriendo, setAbriendo] = useState(false);
  const abrir = async () => {
    const w = window.open("about:blank", "_blank");
    if (ENVIRONMENT !== "production") {
      // En Preview no hay SSO (la EF vive sólo en prod): enlace directo.
      if (w) w.location.href = PROPIEDADES_URL;
      else window.open(PROPIEDADES_URL, "_blank", "noopener");
      return;
    }
    setAbriendo(true);
    try {
      const { data, error } = await supabase.functions.invoke("sso-socios-propiedades", { body: {} });
      const url = !error && typeof data?.url === "string" ? data.url : PROPIEDADES_URL;
      if (w) w.location.href = url;
      else window.location.assign(url);
      if (error) toast.info("Abrimos el portal de propiedades; es posible que debas iniciar sesión.");
    } catch {
      if (w) w.location.href = PROPIEDADES_URL;
    } finally {
      setAbriendo(false);
    }
  };
  return { abrir, abriendo };
}

/** Etapa vigente + % (Estatus del Proyecto) con mini barra. */
function EtapaProyecto({ etapa, pct }: { etapa: string | null; pct: number | null }) {
  if (!etapa && pct == null) return null;
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex min-w-0 items-center justify-between gap-2 text-[11.5px]">
        <span className="truncate text-muted-foreground">
          {etapa ? <EstatusAvanceChip nombre={etapa} porcentaje={pct} variant="inline" ring={false} className="text-[11.5px]" /> : "Avance de obra"}
        </span>
        {!etapa && pct != null && <span className="shrink-0 font-medium tabular-nums text-primary">{Math.round(pct)}%</span>}
      </div>
      {pct != null && <ProgressBar value={pct} className="h-1.5" />}
    </div>
  );
}

function ProyectoSelector({ compact = false }: { compact?: boolean }) {
  const { proyectos, idProyecto, setIdProyecto, proyectoActual } = useSocioProyecto();
  if (!proyectos.length) return null;
  const multi = proyectos.length > 1;
  return (
    <div>
      {!compact && (
        <p className="pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
          Proyecto
        </p>
      )}
      {multi ? (
        <Select value={idProyecto != null ? String(idProyecto) : undefined} onValueChange={(v) => setIdProyecto(Number(v))}>
          <SelectTrigger className="h-9 w-full bg-background text-[13px]">
            <SelectValue placeholder="Selecciona un proyecto" />
          </SelectTrigger>
          <SelectContent>
            {proyectos.map((p) => (
              <SelectItem key={p.id_proyecto} value={String(p.id_proyecto)}>
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate">{p.nombre}</span>
                  {p.porcentaje_avance != null && (
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {p.etapa ? `${p.etapa} · ` : ""}
                      {Math.round(p.porcentaje_avance)}%
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="truncate text-[13px] font-medium text-foreground">{proyectoActual?.nombre}</p>
      )}
      {proyectoActual && <EtapaProyecto etapa={proyectoActual.etapa} pct={proyectoActual.porcentaje_avance} />}
    </div>
  );
}

function LayoutInner({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { items: navFromDb, cargando: navCargando } = usePortalNavEstado(SOCIOS_MENU_ID, iconMap, LayoutDashboard);
  const { isPathAllowed, isLoading: permisosCargando } = useAllowedMenus();
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const { proyectoActual } = useSocioProyecto();
  const { abrir, abriendo } = useVerMisPropiedades();
  const { isImpersonating, target } = useSocioImpersonation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Nav desde BD (menú 37) filtrada por el permiso de lectura del rol. En "Ver como" el menú
  // es el del socio: el rol Socio no tiene Configuración, aunque el admin que impersona sí.
  // Solo se ofrece (y se permite) sin impersonar.
  const items = navFromDb.filter(
    (i) => isPathAllowed(i.path) && !(isImpersonating && i.path === PORTAL_SOCIOS_CONFIG_PATH),
  );
  const navCargandoTodo = navCargando || permisosCargando;
  const enConfiguracion =
    location.pathname === PORTAL_SOCIOS_CONFIG_PATH || location.pathname.startsWith(`${PORTAL_SOCIOS_CONFIG_PATH}/`);

  useEffect(() => {
    if (isImpersonating && enConfiguracion) navigate(PORTAL_SOCIOS_BASE, { replace: true });
  }, [isImpersonating, enConfiguracion, navigate]);

  // La raíz del portal solo se marca exacta: si no, toda subruta marcaría también el inicio.
  const isActive = (item: PortalNavItem) =>
    item.path === PORTAL_SOCIOS_BASE
      ? location.pathname === item.path || location.pathname === `${item.path}/`
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  const go = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const rawName = isImpersonating
    ? target?.nombre || "Socio"
    : profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const initials =
    userName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join("") || "U";

  const sidebar = (
    <PortalSidebar
      portal="Portal Socio"
      contexto={<ProyectoSelector />}
      cargando={navCargandoTodo}
      groups={[{
        items: items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item),
          onSelect: () => go(item.path),
        })),
      }]}
      usuario={{
        nombre: userName,
        rol: isImpersonating ? "Vista socio (Ver como)" : "Socio",
        iniciales: initials,
      }}
      onRegresar={canReturnToAdmin ? () => go("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <div className="flex min-h-screen bg-muted/30 antialiased">
      <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
        {sidebar}
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
          <SheetTitle className="sr-only">Menú del Portal Socio</SheetTitle>
          {sidebar}
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/75 sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu className="size-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-muted-foreground lg:hidden">
              {proyectoActual?.nombre ?? "Portal Socio"}
            </p>
          </div>
          <SocioImpersonationSelector className="max-w-[55vw] sm:max-w-none" />
          {isImpersonating ? (
            // Botón deshabilitado no dispara hover: el tooltip cuelga de un span enfocable.
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <Button variant="outline" size="sm" disabled className="pointer-events-none gap-2" aria-disabled>
                      <ExternalLink className="size-4" />
                      <span className="hidden sm:inline">Ver mis propiedades</span>
                      <span className="sm:hidden">Propiedades</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">No disponible en modo Ver como</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button variant="outline" size="sm" onClick={abrir} disabled={abriendo} className="gap-2">
              {abriendo ? <Loader2 className="size-4 animate-spin" /> : <ExternalLink className="size-4" />}
              <span className="hidden sm:inline">Ver mis propiedades</span>
              <span className="sm:hidden">Propiedades</span>
            </Button>
          )}
        </header>
        <SocioImpersonationBanner />

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}

export default function PortalSociosLayout({ children }: { children: ReactNode }) {
  return (
    <SocioImpersonationProvider>
      <SocioProyectoProvider>
        <LayoutInner>{children}</LayoutInner>
      </SocioProyectoProvider>
    </SocioImpersonationProvider>
  );
}
